/*

  Localstorage.ts

  An interface around HTML5 local storage functionality, if available

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { RACK_SIZE, SavedTile } from "./types";

export interface LocalStorage {
  getLocalTile: (ix: number) => string;
  getLocalTileSq: (ix: number) => string;
  setLocalTile: (ix: number, t: string) => void;
  setLocalTileSq: (ix: number, sq: string) => void;
  clearTiles: () => void;
  saveTiles: (tilesPlaced: SavedTile[]) => void;
  loadTiles: () => SavedTile[];
}

let _hasLocal: boolean | null = null; // Is HTML5 local storage supported by the browser?

function hasLocalStorage(): boolean {
  // Return true if HTML5 local storage is supported by the browser
  if (_hasLocal === null)
    try {
      _hasLocal = ('localStorage' in window) &&
        (window.localStorage !== null) &&
        (window.localStorage !== undefined);
    } catch (e) {
      _hasLocal = false;
    }
  return _hasLocal;
}

class LocalStorageImpl implements LocalStorage {

  _prefix: string;

  constructor(uuid: string) {
    // Constructor for local storage associated with a particular game
    this._prefix = `game.${uuid}`;
  }

  getLocalTile(ix: number) {
    return window.localStorage[`${this._prefix}.tile.${ix}.t`];
  }

  getLocalTileSq(ix: number) {
    return window.localStorage[`${this._prefix}.tile.${ix}.sq`];
  }

  setLocalTile(ix: number, t: string) {
    window.localStorage[`${this._prefix}.tile.${ix}.t`] = t;
  }

  setLocalTileSq(ix: number, sq: string) {
    window.localStorage[`${this._prefix}.tile.${ix}.sq`] = sq;
  }

  clearTiles() {
    // Clean up local storage when game is over
    try {
      const ls = window.localStorage;
      for (let i = 1; i <= RACK_SIZE; i++) {
        ls.removeItem(`${this._prefix}.tile.${i}.sq`);
        ls.removeItem(`${this._prefix}.tile.${i}.t`);
      }
    }
    catch (e) {
    }
  }

  saveTiles(tilesPlaced: SavedTile[]) {
    // Save tile locations in local storage
    let i: number;
    for (i = 0; i < tilesPlaced.length; i++) {
      // Store this placed tile in local storage
      const sq = tilesPlaced[i].sq;
      const tile = tilesPlaced[i].tile;
      // Set the placed tile's square
      this.setLocalTileSq(i + 1, sq);
      // Set the letter (or ?+letter if undefined)
      this.setLocalTile(i + 1, tile);
    }
    // Erase all remaining positions in local storage
    for (; i < RACK_SIZE; i++) {
      this.setLocalTileSq(i + 1, "");
      this.setLocalTile(i + 1, "");
    }
  }

  loadTiles() {
    // Return the saved tile locations
    let sq: string, tile: string;
    let tp: SavedTile[] = [];
    for (let i = 0; i < RACK_SIZE; i++) {
      sq = this.getLocalTileSq(i + 1);
      tile = this.getLocalTile(i + 1);
      if (sq && tile)
        tp.push({sq: sq, tile: tile});
    }
    return tp;
  }

} // class LocalStorageImpl

class NoLocalStorageImpl implements LocalStorage {

  // This class is used if the browser does not implement local storage

  constructor() { }

  getLocalTile(ix: number) { return ""; }
  getLocalTileSq(ix: number) { return ""; }
  setLocalTile(ix: number, t: string) { }
  setLocalTileSq(ix: number, sq: string) { }
  clearTiles() { }
  saveTiles(tilesPlaced: SavedTile[]) { }
  loadTiles(): SavedTile[] { return []; }

} // class NoLocalStorageImpl

export const getLocalStorage = (uuid: string): LocalStorage => {
  return hasLocalStorage() ? new LocalStorageImpl(uuid) : new NoLocalStorageImpl();
}
